import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { getAuditAlertsPageload } from '../../api/audit';
import useLoadingOverlay from '../../hooks/useLoadingOverlay';
export const AuditAlertsContext = createContext({
    selectedAuditExpense: null,
    setSelectedAuditExpense: () => { },
    updateAuditFlagStatus: () => { },
    auditExpenses: null,
    auditDefinitions: null,
    loading: true,
    error: null,
    sidebarOpen: false,
    toggleSidebar: () => { }
});
export default function AuditAlertsProvider({ children }) {
    const { applyLoadingState, removeLoadingState } = useLoadingOverlay();
    const [auditExpenses, setAuditExpenses] = useState(null);
    const [auditDefinitions, setAuditDefinitions] = useState(null);
    const [selectedAuditExpense, setSelectedAuditExpense] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const refreshAuditAlerts = useCallback(async (isInitialLoad = false) => {
        try {
            if (isInitialLoad) {
                setLoading(true);
            }
            const requestedAuditAlertsPageload = await getAuditAlertsPageload();
            setAuditExpenses(requestedAuditAlertsPageload.AuditExpenses.map((auditExpense) => ({
                ...auditExpense,
                id: auditExpense.ExpPK
            })));
            setAuditDefinitions(requestedAuditAlertsPageload.AuditDefs);
        }
        catch (e) {
            setError(e.message);
        }
        finally {
            if (isInitialLoad) {
                setLoading(false);
            }
        }
    }, []);
    const toggleSidebar = useCallback(() => {
        setSidebarOpen((prev) => !prev);
    }, []);
    useEffect(() => {
        (async () => {
            try {
                applyLoadingState();
                await refreshAuditAlerts(true);
            }
            catch (e) {
                setError(e.message);
            }
            finally {
                removeLoadingState();
            }
        })();
    }, [refreshAuditAlerts, applyLoadingState, removeLoadingState]);
    const updateAuditFlagStatus = useCallback(async (AuditFlagPk, status) => {
        await refreshAuditAlerts();
        setSelectedAuditExpense((prev) => {
            if (prev === null) {
                return null;
            }
            return {
                ...prev,
                Flags: prev.Flags.map((auditFlag) => {
                    if (auditFlag.AuditFlagPK === AuditFlagPk) {
                        return {
                            ...auditFlag,
                            Status: status
                        };
                    }
                    return auditFlag;
                })
            };
        });
    }, [refreshAuditAlerts]);
    const contextValue = useMemo(() => ({
        auditExpenses,
        auditDefinitions,
        updateAuditFlagStatus,
        loading,
        error,
        sidebarOpen,
        toggleSidebar,
        selectedAuditExpense,
        setSelectedAuditExpense
    }), [
        auditExpenses,
        auditDefinitions,
        updateAuditFlagStatus,
        loading,
        error,
        sidebarOpen,
        toggleSidebar,
        selectedAuditExpense
    ]);
    return (_jsx(AuditAlertsContext.Provider, { value: contextValue, children: children }));
}
